import Phaser from 'phaser';
import { audioManager } from '@/systems/AudioManager';
import { Layout, Palette, Typography } from '@/ui/theme';

/**
 * Botão circular de mute/desmute.
 *
 * Único ponto de UI hoje para a configuração de áudio. Quando tivermos
 * a SettingsScene completa, este botão pode virar atalho ou ser
 * substituído — mas pra criança poder calar o jogo na rua, ele já tem
 * que existir desde o início.
 *
 * Posicionamento usual: canto superior direito. Tamanho respeita 48px
 * (alvo de toque, CLAUDE.md §8).
 */
export class MuteToggle extends Phaser.GameObjects.Container {
  private readonly background: Phaser.GameObjects.Graphics;
  private readonly icon: Phaser.GameObjects.Text;
  private readonly size: number;

  constructor(scene: Phaser.Scene, x: number, y: number, size: number = Layout.minTouchTarget) {
    super(scene, x, y);
    this.size = Math.max(size, Layout.minTouchTarget);

    this.background = scene.add.graphics();
    this.icon = scene.add
      .text(0, 0, this.iconText(), {
        fontFamily: Typography.fontFamily,
        fontSize: `${Math.round(this.size * 0.55)}px`,
        color: Palette.textPrimaryCss,
      })
      .setOrigin(0.5);

    this.add([this.background, this.icon]);
    this.drawBackground();

    this.setSize(this.size, this.size);
    this.setInteractive({ useHandCursor: true });
    this.on(Phaser.Input.Events.POINTER_OVER, () => this.drawBackground(true));
    this.on(Phaser.Input.Events.POINTER_OUT, () => this.drawBackground(false));
    this.on(Phaser.Input.Events.POINTER_DOWN, () => this.setScale(0.92));
    this.on(Phaser.Input.Events.POINTER_UP, () => {
      this.setScale(1);
      audioManager.toggleMuted();
      this.refresh();
    });

    scene.add.existing(this);
  }

  refresh(): void {
    this.icon.setText(this.iconText());
    this.drawBackground();
  }

  private iconText(): string {
    return audioManager.isMuted() ? '🔇' : '🔊';
  }

  private drawBackground(hover = false): void {
    this.background.clear();
    const color = audioManager.isMuted() ? 0x2a2a4a : Palette.primary;
    this.background.fillStyle(hover ? Palette.accent : color, 1);
    this.background.fillCircle(0, 0, this.size / 2);
  }
}
